import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { flattenChildren } from './slideUtils'
import { useDeckProgress } from '../lib/useDeckProgress'
import type { Slide } from './slides/types'

interface DeckContextValue {
  moduleTitle: string
  onExit?: () => void
  moduleId?: string
  serverIndex?: number
}

export const DeckContext = createContext<DeckContextValue>({ moduleTitle: '' })

export function DeckProvider({
  moduleTitle,
  onExit,
  moduleId,
  serverIndex,
  children,
}: {
  moduleTitle: string
  onExit?: () => void
  moduleId?: string
  serverIndex?: number
  children: ReactNode
}) {
  return (
    <DeckContext.Provider value={{ moduleTitle, onExit, moduleId, serverIndex }}>
      {children}
    </DeckContext.Provider>
  )
}

interface SlideDeckProps {
  /** MDX path: slides come in as children and are flattened. */
  children?: ReactNode
  /** Hand-crafted path (ADR-0018): pre-built Slide[] takes precedence over children. */
  slides?: Slide[]
}

export function SlideDeck({ children, slides: handCraftedSlides }: SlideDeckProps) {
  const { moduleTitle, onExit, moduleId, serverIndex } = useContext(DeckContext)
  // flattenChildren must run during render (not in useMemo) because it may
  // call MDX's _createMdxContent function, which internally uses useContext.
  const slides = handCraftedSlides
    ? handCraftedSlides.map((s) => s.render())
    : flattenChildren(children)
  const total = slides.length
  const { initialIndex, persist } = useDeckProgress(moduleId, total, serverIndex)
  const [index, setIndex] = useState(initialIndex)
  const slideRefs = useRef<Map<number, HTMLElement>>(new Map())
  const touchStartX = useRef<number | null>(null)
  const [searchParams] = useSearchParams()
  const scrollHref = (() => {
    const params = new URLSearchParams(searchParams)
    params.set('view', 'scroll')
    return `?${params.toString()}`
  })()

  useEffect(() => {
    if (index >= total && total > 0) setIndex(total - 1)
  }, [index, total])

  const goNext = useCallback(() => {
    setIndex((i) => (i >= total - 1 ? i : i + 1))
  }, [total])

  const goPrev = useCallback(() => {
    setIndex((i) => (i <= 0 ? i : i - 1))
  }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLElement) {
        const tag = e.target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
        if (e.target.isContentEditable) return
      }
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault()
        goNext()
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault()
        goPrev()
      } else if (e.key === 'Home') {
        e.preventDefault()
        setIndex(0)
      } else if (e.key === 'End') {
        e.preventDefault()
        setIndex(total - 1)
      } else if (e.key === 'Escape' && onExit) {
        e.preventDefault()
        onExit()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [goNext, goPrev, total, onExit])

  // Hide the global app header while the deck is active.
  useEffect(() => {
    document.body.classList.add('slide-deck-active')
    return () => document.body.classList.remove('slide-deck-active')
  }, [])

  // Focus the active slide's heading for accessibility on slide change.
  useEffect(() => {
    const el = slideRefs.current.get(index)
    if (!el) return
    const heading = el.querySelector<HTMLElement>('h1, h2')
    if (heading) {
      heading.setAttribute('tabindex', '-1')
      heading.focus({ preventScroll: true })
    }
  }, [index])

  // Persist current slide on every change.
  useEffect(() => {
    persist(index)
  }, [index, persist])

  if (total === 0) {
    return <div className="slide-deck-empty">No slides.</div>
  }

  const pct = ((index + 1) / total) * 100

  return (
    <div
      className="slide-deck"
      role="region"
      aria-roledescription="carousel"
      aria-label={`${moduleTitle} slides`}
    >
      <header className="slide-chrome">
        <div className="slide-chrome__left">
          {onExit && (
            <button
              type="button"
              onClick={onExit}
              aria-label="Exit module"
              className="slide-chrome__exit"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M3 3l8 8M11 3l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          <Link to={scrollHref} className="slide-chrome__scroll-link">
            <span className="slide-chrome__scroll-link-long">View as scroll</span>
            <span className="slide-chrome__scroll-link-short" aria-hidden="true">Scroll</span>
          </Link>
        </div>
        <div className="slide-chrome__title">{moduleTitle}</div>
        <div className="slide-chrome__right" aria-hidden="true" />
      </header>

      <div
        className="slide-deck__stage"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0]?.clientX ?? null
        }}
        onTouchEnd={(e) => {
          const start = touchStartX.current
          touchStartX.current = null
          if (start == null) return
          const end = e.changedTouches[0]?.clientX
          if (end == null) return
          const dx = end - start
          if (Math.abs(dx) < 48) return
          if (dx < 0) goNext()
          else goPrev()
        }}
        onClick={(e) => {
          const target = e.target as HTMLElement
          if (
            target.closest(
              "a, button, input, select, textarea, [role='button'], [role='checkbox'], [role='radio']",
            )
          ) {
            return
          }
          const rect = e.currentTarget.getBoundingClientRect()
          const x = e.clientX - rect.left
          const third = rect.width / 3
          if (x < third) goPrev()
          else if (x > rect.width - third) goNext()
        }}
      >
        <div
          aria-live="polite"
          aria-atomic="true"
          className="slide-deck__sr-only"
        >
          {`Slide ${index + 1} of ${total}`}
        </div>

        {slides.map((slide, i) => (
          <div
            key={handCraftedSlides?.[i]?.id ?? i}
            ref={(el) => {
              if (el) slideRefs.current.set(i, el)
              else slideRefs.current.delete(i)
            }}
            className="slide-wrapper"
            data-active={i === index ? 'true' : 'false'}
            aria-hidden={i !== index}
          >
            <section
              data-slide
              className="slide-surface"
            >
              {slide}
            </section>
          </div>
        ))}
      </div>

      <div className="chapter-bar" aria-hidden="true">
        <div className="chapter-bar__track">
          <div className="chapter-bar__fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="chapter-bar__meta">
          <span className="chapter-bar__count">
            {index + 1} <span className="chapter-bar__sep">/</span> {total}
          </span>
        </div>
      </div>
    </div>
  )
}
